//================= [JS] ======================================
import {onBeforeUnmount, ref, shallowRef} from 'vue';
import {useEventBus} from '@simpleforyou/client-core';
import {Events} from '@/common/utils/Events';
import {useApiClient} from '@/common/api/useApiClient';
import {IndividualCoachingApi} from '@/common/api/individualCoaching/IndividualCoachingApi';
import type {CallSignalKind, ICallJoin, ICallSignal} from '@/types/components/individualCoaching/IIndividualCoaching';

export type CallStatus = 'idle' | 'preview' | 'joining' | 'waiting' | 'connecting' | 'connected' | 'reconnecting' | 'ended' | 'error';

/** Error keys below `individualCoaching.call.*`. */
export type CallError = 'unsupported' | 'noDevices' | 'full' | 'outsideWindow' | 'failed';

/** Rough connection quality from round-trip time and packet loss. */
export type CallQuality = 'good' | 'medium' | 'poor';

export interface CallDevice {
  deviceId: string;
  label: string;
}

/** What the other side tells about itself over the data channel. */
export interface RemoteState {
  muted: boolean;
  videoOff: boolean;
  sharing: boolean;
  /** T00O4VGPVISQ — id of the stream that carries the shared screen, so it is told apart from the camera. */
  screenStreamId?: string | null;
}

/** Anything else sent over the data channel, e.g. the recording handshake. */
export type CallMessage = {type: string} & Record<string, unknown>;

const CHANNEL_LABEL = 'coaching';

/**
 * T00VQIQVIXFU — ask for 720p at 30 fps. Without it most browsers hand out
 * 640×480. Only `ideal`: a weaker camera delivers what it can instead of
 * failing with an OverconstrainedError.
 */
export const CAMERA_QUALITY: MediaTrackConstraints = {width: {ideal: 1280}, height: {ideal: 720}, frameRate: {ideal: 30}};

/** Upper limit for the outgoing picture; the browser still goes lower on a weak line. */
export const VIDEO_MAX_BITRATE = 2_500_000;
/** T00O4VGPVISQ — while the screen is shared the camera steps back, so the line carries both. */
export const CAMERA_WHILE_SHARING_BITRATE = 600_000;

/**
 * T00SVJDN3RK4 — one 1:1 call in the browser: WebRTC between coach and client,
 * signalling through the API (POST signal) and back over the SSE stream.
 *
 * Who offers: the one who joins second. They know the other is already there
 * (`peerPresent`), the first one only learns it from `peer-joined` and waits
 * for the offer. That way the two never offer at the same time.
 *
 * Losing the connection (Wi-Fi switch, sleep) triggers an ICE restart. The
 * coach restarts; the client asks the coach to (`restart`), so again only one
 * side offers.
 *
 * T00VLGPYIV5X — a preview with device choice before joining, a data channel
 * for each side's state (and the recording handshake), call duration and a
 * rough quality from getStats.
 */
export function useCoachingCall(clientId: string, sessionId: string, occurrenceDate: string) {
  const apiClient = useApiClient();
  const eventBus = useEventBus();

  const status = ref<CallStatus>('idle');
  const error = ref<CallError | null>(null);
  const join = ref<ICallJoin | null>(null);
  const peerName = ref<string | null>(null);
  const localStream = shallowRef<MediaStream | null>(null);
  const remoteStream = shallowRef<MediaStream | null>(null);
  const muted = ref(false);
  const videoOff = ref(false);
  const sharing = ref(false);
  const remoteState = ref<RemoteState>({muted: false, videoOff: false, sharing: false, screenStreamId: null});
  /** T00O4VGPVISQ — the screen the other side shares, next to their camera. */
  const remoteScreenStream = shallowRef<MediaStream | null>(null);
  const cameras = ref<CallDevice[]>([]);
  const microphones = ref<CallDevice[]>([]);
  const cameraId = ref<string | null>(null);
  const microphoneId = ref<string | null>(null);
  const elapsed = ref(0);
  const quality = ref<CallQuality | null>(null);
  const canShareScreen = typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';

  let connection: RTCPeerConnection | null = null;
  let channel: RTCDataChannel | null = null;
  let pendingCandidates: RTCIceCandidateInit[] = [];
  let screenTrack: MediaStreamTrack | null = null;
  let screenStream: MediaStream | null = null;
  let screenSender: RTCRtpSender | null = null;
  /** Every stream the other side sends, by id: the camera (with sound) and maybe a screen. */
  const remoteStreams = new Map<string, MediaStream>();
  let connectedSince: number | null = null;
  let ticker: ReturnType<typeof setInterval> | null = null;
  let lastStats: {lost: number; received: number} | null = null;
  const messageHandlers = new Set<(message: CallMessage) => void>();

  //================= [SIGNALLING] =============================

  async function send(kind: CallSignalKind, data: Record<string, unknown> | null = null): Promise<void> {
    await apiClient.post(IndividualCoachingApi.call(clientId, sessionId, occurrenceDate, 'signal'), {kind, data}).catch(() => {
      // A lost signal shows as a stalled connection; the restart path recovers it.
    });
  }

  /**
   * T00WENZFLHM2 — signals are handled one after the other. Two offers close
   * together (a restart from both sides) used to interleave: both answered,
   * the second answer hit a connection that was already stable again, and the
   * call never recovered.
   */
  let signalQueue: Promise<void> = Promise.resolve();

  /** Runs a negotiation step after the ones before it; a failure does not block the next. */
  function enqueue(step: () => Promise<void>): Promise<void> {
    signalQueue = signalQueue.then(step).catch(() => {
      // The restart path recovers a broken negotiation.
    });
    return signalQueue;
  }

  function onSignal(signal: ICallSignal): Promise<void> {
    return enqueue(() => handleSignal(signal));
  }

  async function handleSignal(signal: ICallSignal): Promise<void> {
    if (!signal || signal.sessionId !== sessionId || signal.occurrenceDate !== occurrenceDate || ['ended', 'idle', 'preview'].includes(status.value)) {
      return;
    }
    switch (signal.kind) {
      case 'peer-joined':
        // The other side (re)joined: they will offer on a fresh connection.
        peerName.value = signal.from ?? peerName.value;
        resetConnection();
        status.value = 'connecting';
        break;
      case 'peer-left':
      case 'hangup':
        resetConnection();
        remoteStream.value = null;
        status.value = 'waiting';
        break;
      case 'offer':
        await acceptOffer(signal.data as unknown as RTCSessionDescriptionInit);
        break;
      case 'answer':
        // An answer only fits our own open offer; a late duplicate is dropped.
        if (connection?.signalingState === 'have-local-offer') {
          await connection.setRemoteDescription(signal.data as unknown as RTCSessionDescriptionInit);
          await flushCandidates();
        }
        break;
      case 'candidate':
        await addCandidate(signal.data as RTCIceCandidateInit);
        break;
      case 'restart':
        if (join.value?.role === 'coach') {
          await restart();
        }
        break;
    }
  }

  //================= [DATA CHANNEL] ===========================

  function attachChannel(next: RTCDataChannel): void {
    channel = next;
    next.onopen = () => sendState();
    next.onmessage = (event) => {
      let message: CallMessage;
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (message.type === 'state') {
        const before = remoteState.value.screenStreamId;
        const screenStreamId = typeof message.screenStreamId === 'string' ? message.screenStreamId : null;
        remoteState.value = {muted: !!message.muted, videoOff: !!message.videoOff, sharing: !!message.sharing, screenStreamId};
        if (before && before !== screenStreamId) {
          remoteStreams.delete(before);
        }
        assignRemoteStreams();
        return;
      }
      messageHandlers.forEach((handler) => handler(message));
    };
  }

  /** Sends over the data channel; false when it is not open (yet). */
  function sendMessage(message: CallMessage): boolean {
    if (channel?.readyState !== 'open') {
      return false;
    }
    channel.send(JSON.stringify(message));
    return true;
  }

  function onMessage(handler: (message: CallMessage) => void): () => void {
    messageHandlers.add(handler);
    return () => messageHandlers.delete(handler);
  }

  function sendState(): void {
    sendMessage({type: 'state', muted: muted.value, videoOff: videoOff.value, sharing: sharing.value, screenStreamId: screenStream?.id ?? null});
  }

  //================= [PEER CONNECTION] ========================

  function createConnection(): RTCPeerConnection {
    const peer = new RTCPeerConnection({iceServers: join.value?.iceServers ?? []});
    localStream.value?.getTracks().forEach((track) => peer.addTrack(track, localStream.value!));
    if (screenTrack && screenStream) {
      screenSender = peer.addTrack(screenTrack, screenStream);
    }
    peer.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      remoteStreams.set(stream.id, stream);
      assignRemoteStreams();
    };
    // T00O4VGPVISQ — a screen added or removed mid-call needs a new offer. Before
    // the call stands the explicit offer covers it; afterwards the queue keeps
    // it apart from other signals, and a rolled-back offer comes back here.
    peer.onnegotiationneeded = () => {
      if (status.value === 'connected' || status.value === 'reconnecting') {
        void enqueue(() => offer());
      }
    };
    peer.ondatachannel = (event) => attachChannel(event.channel);
    peer.onicecandidate = (event) => {
      if (event.candidate) {
        void send('candidate', event.candidate.toJSON() as Record<string, unknown>);
      }
    };
    peer.onconnectionstatechange = () => {
      switch (peer.connectionState) {
        case 'connected':
          status.value = 'connected';
          startTicker();
          // Now every browser has encodings to tune (T00VQIQVIXFU).
          void tuneOutgoing();
          break;
        case 'disconnected':
        case 'failed':
          status.value = 'reconnecting';
          void enqueue(() => restart());
          break;
      }
    };
    return peer;
  }

  /** Camera (with sound) and shared screen of the other side, told apart by the stream id they announce. */
  function assignRemoteStreams(): void {
    const screenId = remoteState.value.screenStreamId ?? null;
    const streams = [...remoteStreams.values()];
    const camera = streams.find((stream) => stream.id !== screenId && stream.getAudioTracks().length > 0)
      ?? streams.find((stream) => stream.id !== screenId)
      ?? null;
    if (camera && camera !== remoteStream.value) {
      remoteStream.value = camera;
    }
    const screen = remoteState.value.sharing && screenId ? remoteStreams.get(screenId) ?? null : null;
    if (screen !== remoteScreenStream.value) {
      remoteScreenStream.value = screen;
    }
  }

  function resetConnection(): void {
    channel?.close();
    channel = null;
    connection?.close();
    connection = null;
    pendingCandidates = [];
    lastStats = null;
    quality.value = null;
    remoteState.value = {muted: false, videoOff: false, sharing: false, screenStreamId: null};
    remoteStreams.clear();
    remoteScreenStream.value = null;
    screenSender = null;
  }

  // Each step keeps its own reference: a reset while awaiting must not hand
  // the rest of the negotiation a closed or missing connection.
  function currentConnection(): RTCPeerConnection {
    if (!connection) {
      connection = createConnection();
    }
    return connection;
  }

  async function offer(iceRestart = false): Promise<void> {
    const peer = currentConnection();
    // The offering side opens the data channel; it has to exist before the offer.
    if (!channel) {
      attachChannel(peer.createDataChannel(CHANNEL_LABEL));
    }
    const description = await peer.createOffer({iceRestart});
    await peer.setLocalDescription(description);
    await send('offer', {type: description.type, sdp: description.sdp});
  }

  async function acceptOffer(description: RTCSessionDescriptionInit): Promise<void> {
    const peer = currentConnection();
    // Both sides offered at once: the coach keeps theirs, the client takes back its own.
    if (peer.signalingState === 'have-local-offer') {
      if (join.value?.role === 'coach') {
        return;
      }
      await peer.setLocalDescription({type: 'rollback'});
    }
    await peer.setRemoteDescription(description);
    await flushCandidates();
    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);
    await send('answer', {type: answer.type, sdp: answer.sdp});
    if (status.value !== 'connected') {
      status.value = 'connecting';
    }
  }

  async function addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!connection || !connection.remoteDescription) {
      pendingCandidates.push(candidate);
      return;
    }
    await connection.addIceCandidate(candidate).catch(() => {
      // An outdated candidate after a restart is harmless.
    });
  }

  async function flushCandidates(): Promise<void> {
    const queued = pendingCandidates;
    pendingCandidates = [];
    for (const candidate of queued) {
      await addCandidate(candidate);
    }
  }

  /**
   * 'disconnected' and then 'failed', plus the client's request, each ask for
   * a restart. One at a time is enough: a second offer while the first is
   * still open would only cross it.
   */
  async function restart(): Promise<void> {
    if (join.value?.role !== 'coach') {
      await send('restart');
      return;
    }
    if (connection?.signalingState === 'have-local-offer') {
      return;
    }
    await offer(true);
  }

  //================= [DURATION & QUALITY] =====================

  function startTicker(): void {
    connectedSince = connectedSince ?? Date.now();
    if (ticker) {
      return;
    }
    let tick = 0;
    ticker = setInterval(() => {
      elapsed.value = Math.floor((Date.now() - (connectedSince ?? Date.now())) / 1000);
      tick += 1;
      if (tick % 2 === 0) {
        void measureQuality();
      }
    }, 1000);
  }

  function stopTicker(): void {
    if (ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  /** Good below 150 ms and 2 % loss, poor above 400 ms or 8 %, medium in between. */
  async function measureQuality(): Promise<void> {
    if (!connection || status.value !== 'connected') {
      return;
    }
    const report = await connection.getStats().catch(() => null);
    if (!report) {
      return;
    }
    let roundTrip: number | null = null;
    let lost = 0;
    let received = 0;
    report.forEach((entry: Record<string, unknown>) => {
      if (entry.type === 'candidate-pair' && entry.nominated && entry.state === 'succeeded' && typeof entry.currentRoundTripTime === 'number') {
        roundTrip = entry.currentRoundTripTime;
      }
      if (entry.type === 'inbound-rtp') {
        lost += Number(entry.packetsLost ?? 0);
        received += Number(entry.packetsReceived ?? 0);
      }
    });
    const lostDelta = lastStats ? lost - lastStats.lost : 0;
    const receivedDelta = lastStats ? received - lastStats.received : 0;
    lastStats = {lost, received};
    const loss = lostDelta + receivedDelta > 0 ? lostDelta / (lostDelta + receivedDelta) : 0;
    const rtt = roundTrip ?? 0;
    quality.value = rtt > 0.4 || loss > 0.08 ? 'poor' : rtt > 0.15 || loss > 0.02 ? 'medium' : 'good';
  }

  //================= [MEDIA] ==================================

  function constraint(id: string | null): MediaTrackConstraints | boolean {
    return id ? {deviceId: {exact: id}} : true;
  }

  function cameraConstraint(id: string | null): MediaTrackConstraints {
    return id ? {...CAMERA_QUALITY, deviceId: {exact: id}} : {...CAMERA_QUALITY};
  }

  /**
   * T00VQIQVIXFU — how the outgoing picture is encoded. The camera stays
   * fluid ('motion'); a shared screen stays sharp so text is readable
   * ('detail', keep the resolution and drop frames instead). Browsers that
   * know none of this simply ignore it.
   */
  async function tuneVideo(track: MediaStreamTrack | null): Promise<void> {
    if (!track || track.kind !== 'video') {
      return;
    }
    const screen = track === screenTrack;
    track.contentHint = screen ? 'detail' : 'motion';
    const sender = connection?.getSenders().find((candidate) => candidate.track === track);
    if (!sender?.getParameters || !sender.setParameters) {
      return;
    }
    const parameters = sender.getParameters() as RTCRtpSendParameters & {degradationPreference?: string};
    // Firefox has no encodings before the connection is negotiated; tuned again once connected.
    if (!parameters.encodings?.length) {
      return;
    }
    parameters.encodings[0].maxBitrate = screen || !screenTrack ? VIDEO_MAX_BITRATE : CAMERA_WHILE_SHARING_BITRATE;
    parameters.degradationPreference = screen ? 'maintain-resolution' : 'maintain-framerate';
    await sender.setParameters(parameters).catch(() => undefined);
  }

  function cameraTrack(): MediaStreamTrack | null {
    return localStream.value?.getVideoTracks()[0] ?? null;
  }

  /** Camera and, while sharing, the screen: each with its own settings. */
  async function tuneOutgoing(): Promise<void> {
    await tuneVideo(cameraTrack());
    await tuneVideo(screenTrack);
  }

  /** Swaps the camera in its own sender; the screen sender stays as it is. */
  async function replaceCamera(previous: MediaStreamTrack | null, track: MediaStreamTrack | null): Promise<void> {
    const sender = connection?.getSenders().find((candidate) => candidate !== screenSender && (candidate.track === previous || candidate.track?.kind === 'video'));
    await sender?.replaceTrack(track);
    await tuneVideo(track);
  }

  async function openMedia(): Promise<MediaStream | null> {
    try {
      return await navigator.mediaDevices.getUserMedia({audio: constraint(microphoneId.value), video: cameraConstraint(cameraId.value)});
    } catch {
      // No camera (or it is blocked): an audio call still beats no call.
      return navigator.mediaDevices.getUserMedia({audio: constraint(microphoneId.value), video: false}).catch(() => null);
    }
  }

  /** Labels are only there once the user allowed access, so this runs after getUserMedia. */
  async function loadDevices(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices?.().catch(() => []) ?? [];
    const toDevice = (device: MediaDeviceInfo, index: number): CallDevice => ({deviceId: device.deviceId, label: device.label || `${device.kind} ${index + 1}`});
    cameras.value = devices.filter((device) => device.kind === 'videoinput').map(toDevice);
    microphones.value = devices.filter((device) => device.kind === 'audioinput').map(toDevice);
    cameraId.value = localStream.value?.getVideoTracks()[0]?.getSettings?.().deviceId ?? cameraId.value ?? cameras.value[0]?.deviceId ?? null;
    microphoneId.value = localStream.value?.getAudioTracks()[0]?.getSettings?.().deviceId ?? microphoneId.value ?? microphones.value[0]?.deviceId ?? null;
  }

  /** Swaps camera or microphone, also mid-call: the new track replaces the old one in the connection. */
  async function selectDevice(kind: 'video' | 'audio', deviceId: string): Promise<void> {
    const fresh = await navigator.mediaDevices.getUserMedia(kind === 'video' ? {video: cameraConstraint(deviceId)} : {audio: {deviceId: {exact: deviceId}}}).catch(() => null);
    const track = fresh?.getTracks()[0];
    if (!track) {
      return;
    }
    track.enabled = kind === 'video' ? !videoOff.value : !muted.value;
    const previousCamera = cameraTrack();
    const others = localStream.value?.getTracks().filter((existing) => existing.kind !== kind) ?? [];
    localStream.value?.getTracks().filter((existing) => existing.kind === kind).forEach((existing) => existing.stop());
    localStream.value = new MediaStream([...others, track]);
    if (kind === 'video') {
      cameraId.value = deviceId;
    } else {
      microphoneId.value = deviceId;
    }
    if (kind === 'video') {
      await replaceCamera(previousCamera, track);
      return;
    }
    await connection?.getSenders().find((sender) => sender.track?.kind === kind)?.replaceTrack(track);
  }

  function toggleMute(): void {
    muted.value = !muted.value;
    localStream.value?.getAudioTracks().forEach((track) => {
      track.enabled = !muted.value;
    });
    sendState();
  }

  function toggleVideo(): void {
    videoOff.value = !videoOff.value;
    localStream.value?.getVideoTracks().forEach((track) => {
      track.enabled = !videoOff.value;
    });
    sendState();
  }

  async function toggleScreenShare(): Promise<void> {
    if (sharing.value) {
      stopScreenShare();
      return;
    }
    if (!canShareScreen) {
      return;
    }
    const display = await navigator.mediaDevices.getDisplayMedia({video: true}).catch(() => null);
    const track = display?.getVideoTracks()[0];
    if (!track) {
      return;
    }
    // T00O4VGPVISQ — the screen comes as a second picture; the camera stays on.
    screenTrack = track;
    screenStream = new MediaStream([track]);
    sharing.value = true;
    track.onended = () => stopScreenShare();
    sendState();
    if (connection) {
      screenSender = connection.addTrack(track, screenStream);
    }
    await tuneOutgoing();
  }

  function stopScreenShare(): void {
    screenTrack?.stop();
    screenTrack = null;
    screenStream = null;
    sharing.value = false;
    if (connection && screenSender) {
      try {
        connection.removeTrack(screenSender);
      } catch {
        // A closed connection has nothing left to remove.
      }
    }
    screenSender = null;
    sendState();
    void tuneOutgoing();
  }

  /** The current screen share track, for the recording; null without sharing. */
  function currentScreenTrack(): MediaStreamTrack | null {
    return screenTrack;
  }

  //================= [LIFECYCLE] ==============================

  function supported(): boolean {
    return typeof RTCPeerConnection !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  /** Camera and microphone before joining: the user checks picture, sound and devices first. */
  async function preview(): Promise<void> {
    error.value = null;
    if (!supported()) {
      fail('unsupported');
      return;
    }
    const stream = localStream.value ?? await openMedia();
    if (!stream) {
      fail('noDevices');
      return;
    }
    localStream.value = stream;
    await loadDevices();
    status.value = 'preview';
  }

  async function start(): Promise<void> {
    error.value = null;
    if (!supported()) {
      fail('unsupported');
      return;
    }
    status.value = 'joining';
    const stream = localStream.value ?? await openMedia();
    if (!stream) {
      fail('noDevices');
      return;
    }
    localStream.value = stream;

    try {
      const response = await apiClient.post<ICallJoin>(IndividualCoachingApi.call(clientId, sessionId, occurrenceDate, 'join'), {});
      join.value = response.data;
    } catch (caught) {
      const code = (caught as {response?: {status?: number}})?.response?.status;
      stopMedia();
      fail(code === 409 ? 'full' : code === 422 ? 'outsideWindow' : 'failed');
      return;
    }
    peerName.value = join.value.peerName;
    if (join.value.peerPresent) {
      status.value = 'connecting';
      await offer();
    } else {
      status.value = 'waiting';
    }
  }

  async function hangup(): Promise<void> {
    if (status.value === 'idle' || status.value === 'ended') {
      return;
    }
    const wasInCall = status.value !== 'preview' && status.value !== 'error';
    status.value = 'ended';
    stopTicker();
    resetConnection();
    stopMedia();
    remoteStream.value = null;
    if (!wasInCall) {
      return;
    }
    await apiClient.post(IndividualCoachingApi.call(clientId, sessionId, occurrenceDate, 'leave'), {}).catch(() => {
      // The room forgets a participant anyway once the window closes.
    });
  }

  function stopMedia(): void {
    screenTrack?.stop();
    screenTrack = null;
    screenStream = null;
    screenSender = null;
    sharing.value = false;
    localStream.value?.getTracks().forEach((track) => track.stop());
    localStream.value = null;
  }

  function fail(reason: CallError): void {
    error.value = reason;
    status.value = 'error';
  }

  const listener = (signal: ICallSignal) => {
    void onSignal(signal);
  };
  eventBus.on(Events.CALL_SIGNAL, listener);
  onBeforeUnmount(() => {
    eventBus.off?.(Events.CALL_SIGNAL, listener);
    stopTicker();
    void hangup();
  });

  return {
    status,
    error,
    join,
    peerName,
    localStream,
    remoteStream,
    remoteScreenStream,
    muted,
    videoOff,
    sharing,
    remoteState,
    cameras,
    microphones,
    cameraId,
    microphoneId,
    elapsed,
    quality,
    canShareScreen,
    preview,
    start,
    hangup,
    selectDevice,
    toggleMute,
    toggleVideo,
    toggleScreenShare,
    currentScreenTrack,
    sendMessage,
    onMessage,
    onSignal,
    measureQuality,
  };
}
